import * as React from "react";
import { createPortal } from "react-dom";
import PortfolioDetailGallery from "./portfolio-detail-gallery";

const isPortfolioDetailPath = (pathname) =>
  /^\/portfolio\/[^/]+\/?$/.test(pathname);

const getOrCreateHost = () => {
  if (typeof document === "undefined") return null;

  let host = document.getElementById("portfolio-gallery-persistent-host");
  if (!host) {
    host = document.createElement("div");
    host.id = "portfolio-gallery-persistent-host";
    document.body.appendChild(host);
  }

  return host;
};

const PortfolioDetailFrame = ({ children, location, pageContext }) => {
  const activeStrapiId = pageContext?.strapi_id;
  const onDetailPage = isPortfolioDetailPath(location.pathname);
  const hostRef = React.useRef(null);
  const [hostReady, setHostReady] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!onDetailPage) {
      const host = document.getElementById("portfolio-gallery-persistent-host");
      if (host) host.remove();
      hostRef.current = null;
      setHostReady(false);
      return undefined;
    }

    const hostEl = getOrCreateHost();
    hostRef.current = hostEl;
    setHostReady(true);

    const syncPosition = () => {
      const anchor = document.getElementById("portfolio-gallery-anchor");
      if (!anchor || !hostEl) return;

      const rect = anchor.getBoundingClientRect();
      if (rect.width < 1) return;

      hostEl.style.position = "fixed";
      hostEl.style.top = `${rect.top}px`;
      hostEl.style.left = `${rect.left}px`;
      hostEl.style.width = `${rect.width}px`;
      hostEl.style.height = `${Math.max(rect.height, 130)}px`;
      hostEl.style.visibility = "visible";
    };

    syncPosition();

    const raf = requestAnimationFrame(syncPosition);
    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncPosition)
        : null;
    const anchor = document.getElementById("portfolio-gallery-anchor");
    const header = anchor?.parentElement;
    if (resizeObserver && header) resizeObserver.observe(header);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
      resizeObserver?.disconnect();
    };
  }, [onDetailPage, location.pathname, activeStrapiId]);

  if (!onDetailPage) {
    return children;
  }

  return (
    <>
      {children}
      {hostReady &&
        hostRef.current &&
        createPortal(
          <PortfolioDetailGallery activeStrapiId={activeStrapiId} />,
          hostRef.current,
        )}
    </>
  );
};

export default PortfolioDetailFrame;
export { isPortfolioDetailPath };
